import { useState, useEffect, createContext, useContext, useCallback } from "react";

interface RouterContext {
	path: string;
	navigate: (to: string) => void;
	params: Record<string, string>;
}

const RouterCtx = createContext<RouterContext>({ path: "/", navigate: () => {}, params: {} });

export function useRouter() {
	return useContext(RouterCtx);
}

export function Router({ children }: { children: React.ReactNode }) {
	const [path, setPath] = useState(window.location.pathname);

	useEffect(() => {
		const handler = () => setPath(window.location.pathname);
		window.addEventListener("popstate", handler);
		return () => window.removeEventListener("popstate", handler);
	}, []);

	const navigate = useCallback((to: string) => {
		window.history.pushState({}, "", to);
		setPath(to);
	}, []);

	return <RouterCtx.Provider value={{ path, navigate, params: {} }}>{children}</RouterCtx.Provider>;
}

interface RouteProps {
	path: string;
	component: React.ComponentType<{ params: Record<string, string> }>;
}

export function Routes({ routes }: { routes: RouteProps[] }) {
	const { path } = useRouter();

	for (const route of routes) {
		const params = matchPath(route.path, path);
		if (params !== null) {
			const Component = route.component;
			return <Component params={params} />;
		}
	}

	return null;
}

function matchPath(pattern: string, path: string): Record<string, string> | null {
	const patternParts = pattern.split("/").filter(Boolean);
	const pathParts = path.split("/").filter(Boolean);

	if (patternParts.length !== pathParts.length) return null;

	const params: Record<string, string> = {};
	for (let i = 0; i < patternParts.length; i++) {
		if (patternParts[i].startsWith(":")) {
			params[patternParts[i].slice(1)] = pathParts[i];
		} else if (patternParts[i] !== pathParts[i]) {
			return null;
		}
	}
	return params;
}

export function Link({
	to,
	children,
	className,
	...props
}: { to: string } & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
	const { navigate } = useRouter();
	return (
		<a
			href={to}
			className={className}
			onClick={(e) => {
				e.preventDefault();
				navigate(to);
			}}
			{...props}
		>
			{children}
		</a>
	);
}
